import { createSegmentActionsPlugin } from '@amplitude/segment-session-replay-plugin';
import { AnalyticsBrowser, type EventProperties } from '@segment/analytics-next';
import Bowser from 'bowser';
import { isDevelopment } from '@/common/utils/checkEnv';
import { getEnv } from '@/common/utils/getEnv';
import { AvoEnv, initAvo, SegmentSourceType, setSystemProperties } from './Avo';

const analytics = AnalyticsBrowser.load({ writeKey: getEnv().segmentWriteKey });

// Initialize Amplitude Session Replay plugin - handles deviceId/sessionId sync automatically
const amplitudeApiKey = getEnv().amplitudeApiKey;
if (amplitudeApiKey) {
  createSegmentActionsPlugin({
    segmentInstance: analytics,
    amplitudeApiKey,
    sessionReplayOptions: {
      sampleRate: 1,
    },
  });
}

export const trackPageView = (pageName: string, properties = {}) => {
  return analytics.page(pageName, properties);
};

export const setUserProperties = (userId: string, properties: EventProperties) => {
  return analytics.identify(userId, properties);
};

const getCurrentPagePath = () => window.location.pathname;

const avoBridge = {
  make: () => {},
  logEvent: (eventName: string, properties: EventProperties) => {
    return analytics.track(eventName, { ...properties, page_path: getCurrentPagePath() });
  },
  setUserProperties,
  identify: (): null => {
    // setUserProperties is taking care of identifying for us with the correct traits
    return null;
  },
  unidentify: () => analytics.identify(null),
  logPage: trackPageView,
  setGroupProperties: (
    _groupType: string,
    groupId: string,
    groupProperties: Record<string, unknown>,
  ) => analytics.group(groupId, groupProperties),
  addCurrentUserToGroup: (_groupType: string, groupId: string) => analytics.group(groupId),
};

const browser = Bowser.getParser(window.navigator.userAgent).getBrowser();
let systemProperties: Parameters<typeof initAvo>[1] = {
  appVersion: 'default',
  webBrowser: browser.name || 'Unknown',
  webBrowserVersion: browser.version || 'Unknown',
  segmentSourceType: SegmentSourceType.BROWSER,
  monetizenowProductIdSystem: [],
  monetizenowAccountIdSystem: '',
  pagePath: getCurrentPagePath(),
};

export const setupAnalytics = async () => {
  const options = {
    env: isDevelopment() ? AvoEnv.Dev : AvoEnv.Prod,
    webDebugger: false,
  };
  initAvo(options, systemProperties, {}, avoBridge);
};

export const setMonetizeNowSystemProperties = ({
  monetizeNowAccountId,
  monetizeNowProductIds,
}: {
  monetizeNowAccountId: string;
  monetizeNowProductIds: string[];
}) => {
  systemProperties = {
    ...systemProperties,
    monetizenowAccountIdSystem: monetizeNowAccountId,
    monetizenowProductIdSystem: monetizeNowProductIds,
  };
  setSystemProperties(systemProperties);
};

export const updatePagePathSystemProperty = () => {
  systemProperties = { ...systemProperties, pagePath: getCurrentPagePath() };
  setSystemProperties(systemProperties);
};
